<template>
  <v-container fluid class="pa-0">
    <!-- Si no hay un ciclo cargado, mostrar alerta -->
    <v-row v-if="!cicloActivo || !cicloActivo.id">
      <v-col cols="12" class="text-center py-10">
        <v-icon size="64" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
        <h3 class="text-h6 grey--text text--darken-2 mt-4">No hay un Ciclo Activo seleccionado</h3>
        <p class="grey--text">Para ver el resumen, primero debes configurar un ciclo presupuestal.</p>
        <v-btn color="primary" outlined @click="cargarCicloActivo" :loading="cargando">
          <v-icon left>mdi-refresh</v-icon> Intentar Cargar Ciclo Reciente
        </v-btn>
      </v-col>
    </v-row>

    <!-- Content -->
    <div v-else>
      <v-row>
        <!-- Tarjeta 1: Presupuesto Total Teórico -->
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="rounded-lg pa-4 text-center fill-height bg-light-blue border-primary">
            <v-icon size="40" color="primary" class="mb-2">mdi-cash-lock</v-icon>
            <h4 class="text-overline grey--text text--darken-2 mb-0">Bolsa Total Asignada</h4>
            <div class="text-h3 font-weight-black primary--text mt-1">
              {{ cicloActivo.bolsa_total | currency }}
            </div>
            <v-divider class="my-3"></v-divider>
            <div class="d-flex justify-center align-center">
              <span class="text-caption font-weight-bold grey--text mr-3">Objetivos Ni/Ri:</span>
              <v-chip color="success" outlined small class="mr-1">NI: {{ cicloActivo.porcentaje_ni }}%</v-chip>
              <v-chip color="warning" outlined small>RI: {{ cicloActivo.porcentaje_ri }}%</v-chip>
            </div>
          </v-card>
        </v-col>

        <!-- Tarjeta 2: Presupuesto Previsto Al Día -->
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="rounded-lg pa-4 text-center fill-height" style="border: 1px solid #9575CD !important; background-color: #F3E5F5;">
            <v-icon size="40" color="deep-purple lighten-1" class="mb-2">mdi-calendar-check</v-icon>
            <h4 class="text-overline grey--text text--darken-2 mb-0">Previsto Al Día</h4>
            <div class="text-h3 font-weight-black deep-purple--text text--darken-1 mt-1">
              {{ presupuestoPrevistoHoy | currency }}
            </div>
            <v-divider class="my-3" style="border-color:#D1C4E9"></v-divider>
            <div class="text-caption grey--text text--darken-2 px-2">
              Suma de límites pre-asignados a hoy.
            </div>
          </v-card>
        </v-col>

        <!-- Tarjeta 3: Gasto Real (APIs) -->
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="rounded-lg pa-4 text-center fill-height">
            <v-icon size="40" :color="termometroColor" class="mb-2">mdi-chart-areaspline</v-icon>
            <h4 class="text-overline grey--text text--darken-2 mb-0">Gasto Ejecutado</h4>
            <div class="text-h3 font-weight-black mt-1" :class="`${termometroColor}--text`">
              {{ totalGastoReales | currency }}
            </div>
            <v-divider class="my-3"></v-divider>
            <div class="d-flex justify-space-between text-caption px-2">
              <span class="font-weight-medium" style="color:#1877F2">Meta: {{ metaSpend | currency }}</span>
              <span class="font-weight-medium" style="color:#EA4335">Google: {{ googleSpend | currency }}</span>
              <span class="font-weight-medium" style="color:#FFB300">Offline: {{ offlineSpend | currency }}</span>
            </div>
          </v-card>
        </v-col>

        <!-- Tarjeta 4: Saldo Disponible -->
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="rounded-lg pa-4 text-center fill-height" :class="{'bg-red-light': saldoDisponible < 0}">
            <v-icon size="40" :color="saldoDisponible >= 0 ? 'success' : 'error'" class="mb-2">
              {{ saldoDisponible >= 0 ? 'mdi-check-decagram' : 'mdi-alert-decagram' }}
            </v-icon>
            <h4 class="text-overline grey--text text--darken-2 mb-0">Saldo Restante</h4>
            <div class="text-h3 font-weight-black mt-1" :class="saldoDisponible >= 0 ? 'success--text' : 'error--text'">
              {{ saldoDisponible | currency }}
            </div>
            <v-divider class="my-3"></v-divider>
            <v-progress-linear
              :value="porcentajeGastoUtilizado"
              :color="termometroColor"
              height="8"
              rounded
            ></v-progress-linear>
            <div class="text-caption grey--text mt-1 text-right">{{ porcentajeGastoUtilizado.toFixed(1) }}% Consumido</div>
          </v-card>
        </v-col>
      </v-row>
      
      <v-row>
         <v-col cols="12">
            <div class="text-center mt-6">
              <v-btn color="primary" depressed @click="sincronizarDatos" :loading="cargando">
                <v-icon left>mdi-refresh</v-icon> Actualizar Dashboard
              </v-btn>
            </div>
         </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
export default {
  name: 'TabConsolidado',
  props: {
    instancia: {
      type: String,
      required: true
    },
    isDirector: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      cargando: false,
      cicloActivo: null,
      metaSpend: 0,
      googleSpend: 0,
      offlineSpend: 0,
      presupuestoPrevistoHoy: 0
    };
  },

  computed: {
    totalGastoReales() {
      return this.metaSpend + this.googleSpend + this.offlineSpend;
    },
    
    saldoDisponible() {
      if(!this.cicloActivo) return 0;
      return this.cicloActivo.bolsa_total - this.totalGastoReales;
    },

    porcentajeGastoUtilizado() {
      if(!this.cicloActivo || this.cicloActivo.bolsa_total === 0) return 0;
      let pct = (this.totalGastoReales / this.cicloActivo.bolsa_total) * 100;
      return pct > 100 ? 100 : pct; // Cap at 100% just for the progress bar visual limit
    },

    termometroColor() {
      const pct = this.porcentajeGastoUtilizado;
      if (pct < 50) return 'success';
      if (pct < 85) return 'warning';
      return 'error';
    }
  },

  mounted() {
    this.cargarCicloActivo();
  },

  methods: {
    async cargarCicloActivo() {
      this.cargando = true;
      try {
        const resCiclos = await this.$http.get(`api/presupuestos/ciclos/${this.instancia}`);
        if (resCiclos.data && resCiclos.data.length > 0) {
          this.cicloActivo = resCiclos.data[0];
          await this.sincronizarApis();
        } else {
          this.cicloActivo = null;
        }
      } catch (error) {
        console.error("Error cargando ciclo", error);
      } finally {
        this.cargando = false;
      }
    },

    async sincronizarDatos() {
      this.cargando = true;
      await this.sincronizarApis(true);
      this.cargando = false;
    },

    async sincronizarApis(forceRefresh = false) {
      if(!this.cicloActivo) return;

      this.metaSpend = 0;
      this.googleSpend = 0;
      this.offlineSpend = 0;
      this.presupuestoPrevistoHoy = 0;

      const dateStart = this.cicloActivo.fecha_inicio.substring(0, 10);
      let dateStop = new Date().toISOString().substring(0, 10);
      const finCiclo = this.cicloActivo.fecha_fin.substring(0, 10);
      if (dateStop > finCiclo) dateStop = finCiclo;

      const fetchPrevistoHoy = async () => {
         try {
           const res = await this.$http.get(`api/presupuestos/rendimiento-diario/${this.cicloActivo.id}?instancia=${this.instancia}`);
           if (res.data && res.data.dias && res.data.dias.length > 0) {
             const hoy = new Date().toISOString().substring(0, 10);
             const dias = res.data.dias;
             
             let diaEncontrado = dias.find(d => d.fecha === hoy);
             if (!diaEncontrado) {
                 if (hoy > res.data.fecha_fin) diaEncontrado = dias[dias.length - 1];
                 else if (hoy < res.data.fecha_inicio) diaEncontrado = dias[0];
             }
             if (diaEncontrado) {
                 this.presupuestoPrevistoHoy = diaEncontrado.acumulado_previsto || 0;
             }
           }
         } catch(e) { console.warn("Error leyendo rendimiento diario", e); }
      };

      const fetchMeta = async () => {
         if (!forceRefresh) {
             try {
                const cache = await this.$http.get(`api/presupuestos/api-cache/${this.cicloActivo.id}/Meta`);
                if (cache.data && cache.data.data) {
                    this.metaSpend = cache.data.data.reduce((acc, curr) => acc + (parseFloat(curr.spend) || 0), 0);
                    return;
                }
             } catch(e) {}
         }
         try {
            const res = await this.$http.post('api/presupuestos/meta/spend', { 
              instancia: this.instancia,
              date_start: dateStart,
              date_stop: dateStop
            });
            if(res.data && res.data.length > 0) {
              this.metaSpend = res.data.reduce((acc, curr) => acc + (parseFloat(curr.spend) || 0), 0);
              this.$http.post('api/presupuestos/api-cache', { id_presupuesto_ciclo: this.cicloActivo.id, canal: 'Meta', response_json: res.data }).catch(e=>e);
            }
         } catch(err) { console.error("Meta:", err); }
      };

      const fetchGoogle = async () => {
         if (!forceRefresh) {
             try {
                const cache = await this.$http.get(`api/presupuestos/api-cache/${this.cicloActivo.id}/Google`);
                if (cache.data && cache.data.data) {
                    this.googleSpend = cache.data.data.reduce((acc, curr) => acc + (parseFloat(curr.spend) || 0), 0);
                    return;
                }
             } catch(e) {}
         }
         try {
            const res = await this.$http.post('api/presupuestos/google/spend', {
              instancia: this.instancia,
              date_start: dateStart,
              date_stop: dateStop
            });
            if(res.data && res.data.length > 0) {
              this.googleSpend = res.data.reduce((acc, curr) => acc + (parseFloat(curr.spend) || 0), 0);
              this.$http.post('api/presupuestos/api-cache', { id_presupuesto_ciclo: this.cicloActivo.id, canal: 'Google', response_json: res.data }).catch(e=>e);
            }
         } catch(err) {
            console.warn("Google API Falló. Buscando fallback manual...", err.message);
            try {
              const resManual = await this.$http.get(`api/presupuestos/gasto_manual/${this.cicloActivo.id}`);
              if(resManual.data) {
                 const gastosGoogle = resManual.data.filter(g => g.canal === 'Google Ads');
                 this.googleSpend = gastosGoogle.reduce((acc, curr) => acc + (parseFloat(curr.monto_gastado) || 0), 0);
              }
            } catch (fallbackErr) {
              console.error("Error leyendo fallback:", fallbackErr);
            }
         }
      };

      const fetchOffline = async () => {
         try {
             const res = await this.$http.get(`api/presupuestos/proyectos/${this.cicloActivo.id}/${this.instancia}`);
             if (res.data && res.data.length > 0) {
                 this.offlineSpend = res.data
                     .filter(p => p.estado === 'Autorizado' || p.estado === 'Ejecutado')
                     .reduce((acc, p) => acc + (p.estado === 'Ejecutado' ? (parseFloat(p.monto_ejecutado) || 0) : (parseFloat(p.monto_solicitado) || 0)), 0);
             }
         } catch(e) { console.warn("Error proyectos offline", e); }
      };

      await Promise.allSettled([fetchPrevistoHoy(), fetchMeta(), fetchGoogle(), fetchOffline()]);
    }
  },

  filters: {
    currency(value) {
      if (typeof value !== "number") return value;
      return "$" + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }
  }
};
</script>

<style scoped>
.bg-light-blue {
  background-color: #F1F8FF;
}
.bg-red-light {
  background-color: #FFF0F0;
  border-color: #FF5252 !important;
}
.border-primary {
  border: 1px solid var(--v-primary-base) !important;
}
</style>
